import React from 'react';
import { motion } from 'framer-motion';
import { Mail, MapPin, Instagram, Heart } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const Footer: React.FC = () => {
  const { t } = useLanguage();

  const footerLinks = [
    {
      title: t('footer.sections.products.title'),
      links: [
        { name: t('footer.sections.products.links.diffusers'), href: '#products' },
        { name: t('footer.sections.products.links.sprays'), href: '#products' },
        { name: t('footer.sections.products.links.candles'), href: '#products' },
        { name: t('footer.sections.products.links.bundles'), href: '#products' },
      ],
    },
    {
      title: t('footer.sections.about.title'),
      links: [
        { name: t('footer.sections.about.links.story'), href: '#stories' },
        { name: t('footer.sections.about.links.ingredients'), href: '#about' },
        { name: t('footer.sections.about.links.process'), href: '#stories' },
        { name: t('footer.sections.about.links.sustainability'), href: '#about' },
      ],
    },
    {
      title: t('footer.sections.support.title'),
      links: [
        { name: t('footer.sections.support.links.contact'), href: '#waitlist' },
        { name: t('footer.sections.support.links.shipping'), href: '#' },
        { name: t('footer.sections.support.links.returns'), href: '#' },
        { name: t('footer.sections.support.links.faq'), href: '#' },
      ],
    },
  ];

  return (
    <footer className="bg-forest text-white">
      <div className="container mx-auto px-6 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Brand */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            viewport={{ once: true }}
          >
            <div className="mb-5">
              <img
                src="/nestbloom-logo.png"
                alt="NestBloom"
                className="h-16 object-contain brightness-0 invert opacity-90"
              />
            </div>
            <p className="text-sm text-white/70 leading-relaxed mb-6">
              {t('footer.brand.description')}
            </p>
            <div className="flex items-center gap-3">
              <a
                href="https://instagram.com/nestbloomherbal"
                target="_blank"
                rel="noreferrer"
                className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center hover:bg-herb-green/40 transition-colors"
                aria-label="Instagram"
              >
                <Instagram className="w-4 h-4" />
              </a>
              <a
                href="https://tiktok.com/@nestbloomherbal"
                target="_blank"
                rel="noreferrer"
                className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center hover:bg-herb-green/40 transition-colors font-bold text-xs"
                aria-label="TikTok"
              >
                TT
              </a>
              <span className="text-xs text-white/40 ml-1">@nestbloomherbal</span>
            </div>
          </motion.div>

          {/* Link columns */}
          {footerLinks.map((section, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: (index + 1) * 0.1 }}
              viewport={{ once: true }}
            >
              <h4 className="text-sm font-bold text-white/90 mb-4 uppercase tracking-wider">
                {section.title}
              </h4>
              <ul className="space-y-2.5">
                {section.links.map((link, i) => (
                  <li key={i}>
                    <a
                      href={link.href}
                      className="text-sm text-white/60 hover:text-white transition-colors duration-200"
                    >
                      {link.name}
                    </a>
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>

        {/* Contact */}
        <div className="mt-10 pt-8 border-t border-white/10 flex flex-wrap gap-6">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-white/10 rounded-lg flex items-center justify-center">
              <Mail className="w-4 h-4 text-herb-sage" />
            </div>
            <div>
              <p className="text-xs text-white/40">{t('footer.contact.email')}</p>
              <p className="text-sm text-white/80">help@nestbloom.com</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-white/10 rounded-lg flex items-center justify-center">
              <MapPin className="w-4 h-4 text-herb-sage" />
            </div>
            <div>
              <p className="text-xs text-white/40">{t('footer.contact.address')}</p>
              <p className="text-sm text-white/80">{t('footer.contact.location')}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="bg-black/20 py-5">
        <div className="container mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-2 text-white/50 text-sm">
              <span>{t('footer.legal.copyright')}</span>
              <Heart className="w-3.5 h-3.5 text-herb-sage" />
            </div>
            <div className="flex gap-6">
              <a href="/privacy" className="text-white/40 hover:text-white text-sm transition-colors">
                {t('footer.legal.privacy')}
              </a>
              <a href="/terms" className="text-white/40 hover:text-white text-sm transition-colors">
                {t('footer.legal.terms')}
              </a>
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-white/5 text-center">
            <p className="text-white/30 text-xs max-w-3xl mx-auto">
              {t('footer.disclaimer.text')}
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
